import React, { useEffect, useMemo, useState } from "react";
import {
  api,
  STORYBOARD_ALL_STATUSES,
  STORYBOARD_STATUS_META,
  type SceneMeta,
  type Storyboard,
  type StoryboardBeat,
  type StoryboardFormat,
  type StoryboardStatus,
} from "../api";

type ToastFn = (kind: "success" | "error" | "info", message: string) => void;

type Props = {
  onOpenComposer: (workspaceReelId: string) => void;
  showToast: ToastFn;
};

type Filter = "all" | StoryboardStatus;

export const Storyboards: React.FC<Props> = ({ onOpenComposer, showToast }) => {
  const [items, setItems] = useState<Storyboard[]>([]);
  const [scenes, setScenes] = useState<SceneMeta[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [convertingId, setConvertingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setErr(null);
    try {
      const [list, sceneList] = await Promise.all([api.storyboardsList(), api.scenes()]);
      setItems(list);
      setScenes(sceneList);
      setSelectedId((prev) => prev && list.some((s) => s.id === prev) ? prev : list[0]?.id ?? null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const counts = useMemo<Record<Filter, number>>(() => {
    const out: Record<string, number> = { all: items.length };
    for (const s of STORYBOARD_ALL_STATUSES) out[s] = 0;
    for (const it of items) out[it.status] = (out[it.status] ?? 0) + 1;
    return out as Record<Filter, number>;
  }, [items]);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((it) => it.status === filter)),
    [items, filter],
  );

  const selected = useMemo(
    () => items.find((it) => it.id === selectedId) ?? null,
    [items, selectedId],
  );

  const onPatch = async (patch: Partial<Storyboard>): Promise<void> => {
    if (!selected) return;
    try {
      const saved = await api.storyboardsPatch(selected.id, patch);
      setItems((prev) => prev.map((it) => (it.id === saved.id ? saved : it)));
    } catch (e) {
      showToast("error", `Speichern fehlgeschlagen: ${(e as Error).message}`);
    }
  };

  const onPatchBeat = (uid: string, patch: Partial<StoryboardBeat>): void => {
    if (!selected) return;
    const beats = selected.beats.map((b) => (b.uid === uid ? { ...b, ...patch } : b));
    void onPatch({ beats });
  };

  const onAddBeat = (): void => {
    if (!selected) return;
    const beat: StoryboardBeat = {
      uid: `b_${Math.random().toString(36).slice(2, 10)}`,
      sceneId: scenes[0]?.id ?? "",
      text: "",
      durationSec: 2,
    };
    void onPatch({ beats: [...selected.beats, beat] });
  };

  const onRemoveBeat = (uid: string): void => {
    if (!selected) return;
    void onPatch({ beats: selected.beats.filter((b) => b.uid !== uid) });
  };

  const onMoveBeat = (uid: string, delta: -1 | 1): void => {
    if (!selected) return;
    const idx = selected.beats.findIndex((b) => b.uid === uid);
    if (idx < 0) return;
    const target = idx + delta;
    if (target < 0 || target >= selected.beats.length) return;
    const beats = [...selected.beats];
    [beats[idx], beats[target]] = [beats[target], beats[idx]];
    void onPatch({ beats });
  };

  const onSetStatus = async (status: StoryboardStatus): Promise<void> => {
    if (!selected) return;
    try {
      const saved = await api.storyboardsSetStatus(selected.id, status);
      setItems((prev) => prev.map((it) => (it.id === saved.id ? saved : it)));
    } catch (e) {
      showToast("error", `Status-Update fehlgeschlagen: ${(e as Error).message}`);
    }
  };

  const onDelete = async (): Promise<void> => {
    if (!selected) return;
    if (!window.confirm(`Storyboard "${selected.title}" löschen?`)) return;
    try {
      await api.storyboardsDelete(selected.id);
      await load();
    } catch (e) {
      showToast("error", `Löschen fehlgeschlagen: ${(e as Error).message}`);
    }
  };

  const onToReel = async (): Promise<void> => {
    if (!selected) return;
    setConvertingId(selected.id);
    try {
      const res = await api.storyboardsToReel(selected.id);
      showToast("success", `Reel "${res.reel.title}" im Workspace erstellt.`);
      await load();
      onOpenComposer(res.reel.id);
    } catch (e) {
      showToast("error", `Composer-Übergabe fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setConvertingId(null);
    }
  };

  const totalDur = selected ? selected.beats.reduce((a, b) => a + (b.durationSec || 0), 0) : 0;

  return (
    <div className="workspace">
      <div className="workspace-head">
        <div className="workspace-head-left">
          <h2>Storyboards</h2>
          <p className="workspace-sub">
            Beat-für-Beat-Outline jeder Idee — Szenen-Picker, Copy-Vorschau, dann Sprung in den Composer.
          </p>
        </div>
        <div className="workspace-head-actions">
          <button className="btn ghost" onClick={() => void load()} disabled={loading}>
            {loading ? "…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      <div className="workspace-filters">
        {(["all", ...STORYBOARD_ALL_STATUSES] as Filter[]).map((f) => {
          const meta = f === "all" ? null : STORYBOARD_STATUS_META[f];
          return (
            <button
              key={f}
              className={`ws-filter ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
              style={meta ? { borderColor: meta.color } : undefined}
            >
              {meta ? <span className="ws-filter-dot" style={{ background: meta.color }} /> : null}
              <span>{f === "all" ? "Alle" : meta?.label}</span>
              <span className="ws-filter-count">{counts[f] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {err && <div className="error-row">{err}</div>}

      {filtered.length === 0 ? (
        <div className="workspace-empty">
          <div className="empty-emoji">🎬</div>
          <h3>Noch keine Storyboards</h3>
          <p>Wandle eine Idee aus dem Pool in ein Storyboard um — der KI-Director skizziert dir die Beats.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16 }}>
          <aside style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filtered.map((it) => {
              const meta = STORYBOARD_STATUS_META[it.status];
              const active = it.id === selectedId;
              return (
                <button
                  key={it.id}
                  onClick={() => setSelectedId(it.id)}
                  style={{
                    textAlign: "left",
                    background: active ? "rgba(59,130,246,0.18)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${active ? meta.color : "rgba(255,255,255,0.1)"}`,
                    borderRadius: 12,
                    padding: 12,
                    color: "inherit",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: 13, opacity: 0.65, marginBottom: 4 }}>
                    <span style={{ color: meta.color }}>● {meta.label}</span> · {it.format} · {it.beats.length} Beats
                  </div>
                  <div style={{ fontWeight: 600 }}>{it.title}</div>
                  {it.reelId ? (
                    <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                      Workspace-Reel: {it.reelId.slice(0, 12)}…
                    </div>
                  ) : null}
                </button>
              );
            })}
          </aside>

          {selected ? (
            <section
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 16,
                padding: 16,
              }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  value={selected.title}
                  onChange={(e) => void onPatch({ title: e.target.value })}
                  style={{ ...inputStyle, flex: 1, fontSize: 18, fontWeight: 600 }}
                />
                <select
                  value={selected.format}
                  onChange={(e) => void onPatch({ format: e.target.value as StoryboardFormat })}
                  style={{ ...inputStyle, width: 110 }}
                >
                  <option value="9:16">9:16</option>
                  <option value="1:1">1:1</option>
                  <option value="16:9">16:9</option>
                </select>
                <select
                  value={selected.status}
                  onChange={(e) => void onSetStatus(e.target.value as StoryboardStatus)}
                  style={{
                    ...inputStyle,
                    width: 160,
                    color: STORYBOARD_STATUS_META[selected.status].color,
                    borderColor: STORYBOARD_STATUS_META[selected.status].color,
                  }}
                >
                  {STORYBOARD_ALL_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STORYBOARD_STATUS_META[s].label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginTop: 8, fontSize: 13, opacity: 0.7 }}>
                Gesamtdauer: <strong>{totalDur.toFixed(1)}s</strong> · Beats: {selected.beats.length}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
                {selected.beats.map((b, i) => (
                  <div
                    key={b.uid}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "32px 200px 1fr 80px 90px",
                      gap: 8,
                      alignItems: "center",
                      background: "rgba(0,0,0,0.18)",
                      borderRadius: 10,
                      padding: 8,
                    }}
                  >
                    <div style={{ opacity: 0.6, textAlign: "center" }}>{i + 1}</div>
                    <select
                      value={b.sceneId}
                      onChange={(e) => onPatchBeat(b.uid, { sceneId: e.target.value })}
                      style={inputStyle}
                    >
                      {scenes.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.id} ({s.category})
                        </option>
                      ))}
                    </select>
                    <input
                      value={b.text}
                      onChange={(e) => onPatchBeat(b.uid, { text: e.target.value })}
                      placeholder="On-screen text"
                      style={inputStyle}
                    />
                    <input
                      type="number"
                      step="0.5"
                      min={0.5}
                      max={6}
                      value={b.durationSec}
                      onChange={(e) =>
                        onPatchBeat(b.uid, {
                          durationSec: Math.max(0.5, Math.min(6, Number(e.target.value) || 2)),
                        })
                      }
                      style={inputStyle}
                    />
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="btn ghost sm" onClick={() => onMoveBeat(b.uid, -1)} disabled={i === 0}>
                        ↑
                      </button>
                      <button
                        className="btn ghost sm"
                        onClick={() => onMoveBeat(b.uid, 1)}
                        disabled={i === selected.beats.length - 1}
                      >
                        ↓
                      </button>
                      <button className="btn danger sm" onClick={() => onRemoveBeat(b.uid)}>
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
                <button className="btn ghost" onClick={onAddBeat} style={{ alignSelf: "flex-start" }}>
                  ＋ Beat hinzufügen
                </button>
              </div>

              <div style={{ marginTop: 24, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn danger" onClick={() => void onDelete()}>
                  Löschen
                </button>
                {selected.reelId ? (
                  <button className="btn primary" onClick={() => onOpenComposer(selected.reelId!)}>
                    Im Composer öffnen
                  </button>
                ) : (
                  <button
                    className="btn primary"
                    onClick={() => void onToReel()}
                    disabled={convertingId === selected.id || selected.beats.length === 0}
                  >
                    {convertingId === selected.id ? "…" : "→ Reel im Composer"}
                  </button>
                )}
              </div>
            </section>
          ) : (
            <div style={{ opacity: 0.6 }}>Wähle links ein Storyboard.</div>
          )}
        </div>
      )}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "#0a0f17",
  color: "#e6edf6",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 14,
  fontFamily: "inherit",
};
